import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Users, UserCheck, UserX, CalendarOff, ClipboardCheck, AlertTriangle, Banknote, FileText, ChevronRight, Sparkles, Activity,
} from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar } from "recharts";
import * as api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useData } from "@/hooks/useData";
import { Button, CardLink, Loader, Progress, SectionCard, StatCard, axisProps, gridProps, tooltipProps } from "@/components/ui";
import { DocumentExpiryTable, HolidayList } from "@/components/hrms";
import { aed } from "@/lib/format";

const ViewAll = ({ to }) => <CardLink as={Link} to={to}>View all <ChevronRight size={12} /></CardLink>;
const greeting = () => { const h = new Date().getHours(); return h < 12 ? "morning" : h < 17 ? "afternoon" : "evening"; };

export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: d } = useData(() => api.dashboard.admin(), []);
  if (!d) return <Loader label="Loading dashboard…" />;

  const isAdmin = user.role === "admin";
  const totalLeaveDays = d.leave_type_usage.reduce((s, x) => s + x.days, 0) || 1;
  const leaveMix = [...d.leave_type_usage].sort((a, b) => b.days - a.days);

  return (
    <>
      {/* Hero greeting */}
      <div className="relative overflow-hidden">
        <div className="absolute -top-16 -right-10 h-40 w-40 rounded-full bg-brand-orange/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-16 -left-8 h-40 w-40 rounded-full bg-brand-blue/10 blur-3xl pointer-events-none" />
        <div className="relative flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-1.5 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-brand-blue">
              <Sparkles size={10} className="text-brand-orange" /> MediLearn HRMS
            </div>
            <h2 className="mt-1.5 text-xl font-extrabold tracking-tight text-brand-blue">Good {greeting()}, {user.name.split(" ")[0]}</h2>
            <p className="text-xs text-slate-500 mt-0.5">Here's what's happening across your workforce today.</p>
          </div>
          <Button variant="primary" icon={<ClipboardCheck size={14} />} onClick={() => navigate("/leave/approvals")}>
            Review approvals{d.pending_approvals ? ` (${d.pending_approvals})` : ""}
          </Button>
        </div>
      </div>

      {/* Stat tiles */}
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4 xl:grid-cols-6">
        <StatCard label="Employees" value={d.total_employees} icon={Users} onClick={() => navigate("/employees")} />
        <StatCard label="Present Today" value={d.present_today} sub={`of ${d.total_employees}`} icon={UserCheck} color="green" onClick={() => navigate("/attendance")} />
        <StatCard label="Absent Today" value={d.absent_today} icon={UserX} color="red" onClick={() => navigate("/attendance")} />
        <StatCard label="On Leave Today" value={d.on_leave_today} icon={CalendarOff} />
        <StatCard label="Pending Approvals" value={d.pending_approvals} icon={ClipboardCheck} color="orange" onClick={() => navigate("/leave/approvals")} />
        {isAdmin
          ? <StatCard label="Docs Expiring" value={d.documents_expiring.length} icon={AlertTriangle} color="red" onClick={() => navigate("/document-expiry")} />
          : <StatCard label="Approved Leaves" value={d.approved_leaves} icon={ClipboardCheck} color="green" />}
      </div>

      {isAdmin && (
        <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
          <StatCard label="Payroll This Month" value={`${aed(d.monthly_payroll_aed / 1000, 1)}K`} sub="AED" icon={Banknote} onClick={() => navigate("/payroll/wps")} />
          <StatCard label="WPS Ready" value={`${d.wps_ready_count}/${d.salary_slips_count}`} icon={FileText} color="orange" onClick={() => navigate("/payroll/wps")} />
          <StatCard label="Approved Leaves" value={d.approved_leaves} icon={ClipboardCheck} color="green" />
          <StatCard label="Rejected Leaves" value={d.rejected_leaves} icon={CalendarOff} color="red" />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <SectionCard title="Monthly Leave Trend" subtitle="Requests by start month" className="lg:col-span-3" action={isAdmin && <ViewAll to="/reports" />}>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={d.leave_trend} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#225881" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#225881" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid {...gridProps} />
              <XAxis dataKey="month" {...axisProps} />
              <YAxis allowDecimals={false} {...axisProps} />
              <Tooltip {...tooltipProps} />
              <Area type="monotone" dataKey="count" name="Requests" stroke="#225881" fill="url(#trendFill)" strokeWidth={2.5} />
            </AreaChart>
          </ResponsiveContainer>
        </SectionCard>

        <div className="lg:col-span-2 space-y-4">
          <div className="neu-card p-4">
            <div className="flex items-center gap-2 mb-3">
              <Activity size={13} className="text-brand-orange" />
              <h3 className="section-title">Leave Breakdown</h3>
              <span className="ml-auto text-[10px] text-slate-400 font-semibold">{totalLeaveDays} days</span>
            </div>
            <div className="space-y-2.5">
              {leaveMix.map((t) => (
                <div key={t.type} className="flex items-center gap-2">
                  <span className="w-24 shrink-0 text-[10px] font-semibold text-slate-500">{t.type}</span>
                  <Progress className="flex-1" value={(t.days / totalLeaveDays) * 100} />
                  <span className="w-6 text-right text-[10px] font-extrabold text-brand-blue tabular-nums shrink-0">{t.days}</span>
                </div>
              ))}
            </div>
          </div>

          <SectionCard title="Upcoming Holidays" action={<ViewAll to="/holidays" />}>
            <HolidayList holidays={d.upcoming_holidays} limit={4} />
          </SectionCard>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <SectionCard title="Leave by Department" subtitle="Days taken" className={isAdmin ? "lg:col-span-2" : "lg:col-span-5"}>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={d.dept_usage} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid {...gridProps} />
              <XAxis dataKey="dept" {...axisProps} />
              <YAxis {...axisProps} />
              <Tooltip {...tooltipProps} />
              <Bar dataKey="days" name="Days" fill="#F5A623" radius={[8, 8, 0, 0]} maxBarSize={40} />
            </BarChart>
          </ResponsiveContainer>
        </SectionCard>

        {isAdmin && (
          <SectionCard title="Document Expiry" count={d.documents_expiring.length} className="lg:col-span-3" bodyClassName="px-0" action={<ViewAll to="/document-expiry" />}>
            <DocumentExpiryTable documents={d.documents_expiring} maxRows={5} />
          </SectionCard>
        )}
      </div>
    </>
  );
}
